import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Users, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import PatientQuickAddModal from "@/components/patients/PatientQuickAddModal";
import { PATIENTS } from "@/constants/testIds";
import { PREDEFINED_TAGS, TAG_COLORS, DEFAULT_TAG_COLOR, tagLabel } from "@/lib/patientConstants";

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadPatients = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients", { params });
      setPatients(data);
    } catch (err) {
      toast({ title: "Error al cargar pacientes", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (tagFilter !== "all") params.tag = tagFilter;
    const timer = setTimeout(() => loadPatients(params), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tagFilter]);

  const handleCreate = async (form) => {
    const payload = { name: form.name.trim(), phone: form.phone.trim() };
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.date_of_birth) payload.date_of_birth = form.date_of_birth;
    if (form.emergency_contact_name?.trim()) payload.emergency_contact_name = form.emergency_contact_name.trim();
    if (form.emergency_contact_phone?.trim()) payload.emergency_contact_phone = form.emergency_contact_phone.trim();
    if (form.allergies?.trim()) payload.allergies = form.allergies.trim();
    const { data } = await api.post("/patients", payload);
    toast({ title: "Paciente creado", description: `${data.name} fue agregado a la lista.` });
    setModalOpen(false);
    navigate(`/pacientes/${data.id}`);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/patients/${deleteTarget.id}`);
      toast({ title: "Paciente eliminado", description: `${deleteTarget.name} y su historial fueron eliminados.` });
      setDeleteTarget(null);
      loadPatients(search.trim() ? { search: search.trim() } : {});
    } catch (err) {
      toast({ title: "Error al eliminar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6" data-testid={PATIENTS.page}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Pacientes</h1>
          <p className="text-base text-muted-foreground mt-1">Fichas clínicas y comerciales de tus pacientes.</p>
        </div>
        <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => setModalOpen(true)} data-testid={PATIENTS.addButton}>
          <Plus className="w-4 h-4" /> Nuevo paciente
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por nombre, teléfono o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={PATIENTS.searchInput}
          />
        </div>
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-48" data-testid={PATIENTS.tagFilterSelect}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las etiquetas</SelectItem>
            {PREDEFINED_TAGS.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-muted-foreground">Cargando pacientes...</p>
        ) : patients.length === 0 ? (
          <div className="p-12 text-center" data-testid={PATIENTS.emptyState}>
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No se encontraron pacientes con esos filtros.</p>
          </div>
        ) : (
          <table className="w-full" data-testid={PATIENTS.table}>
            <thead>
              <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#F3F0EA]">
                <th className="text-left font-medium py-3 px-4">Nombre</th>
                <th className="text-left font-medium py-3 px-4 hidden sm:table-cell">Contacto</th>
                <th className="text-left font-medium py-3 px-4 hidden md:table-cell">Etiquetas</th>
                {user?.role === "admin" && <th className="text-right font-medium py-3 px-4">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {patients.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-border hover:bg-background transition-colors cursor-pointer"
                  onClick={() => navigate(`/pacientes/${p.id}`)}
                  data-testid={`${PATIENTS.rowPrefix}${p.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9">
                        <AvatarImage src={getFileUrl(p.photo_url)} alt={p.name} />
                        <AvatarFallback className="bg-brand-light text-brand text-xs">{p.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-foreground font-medium">{p.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground hidden sm:table-cell">
                    {[p.phone, p.email].filter(Boolean).join(" \u00b7 ") || "-"}
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <div className="flex gap-1 flex-wrap">
                      {(p.tags || []).map((t) => (
                        <Badge key={t} className={`${TAG_COLORS[t] || DEFAULT_TAG_COLOR} border-none font-normal text-[11px]`} data-testid={`${PATIENTS.tagBadgePrefix}${p.id}-${t}`}>
                          {tagLabel(t)}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  {user?.role === "admin" && (
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                        data-testid={`${PATIENTS.deleteButtonPrefix}${p.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PatientQuickAddModal open={modalOpen} onOpenChange={setModalOpen} onCreated={handleCreate} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar a {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara la ficha completa: turnos, tratamientos, fotos, consentimientos y pagos asociados a este paciente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={PATIENTS.deleteCancelButton}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid={PATIENTS.deleteConfirmButton}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
